<template>
  <div class="admin-feedback-page">
    <div class="page-container">
      <header class="page-header">
        <h1 class="page-title">Admin Panel</h1>
        <nav class="admin-nav">
          <router-link to="/admin/users" class="nav-tab">Users</router-link>
          <router-link to="/admin/feedback" class="nav-tab active">Feedback</router-link>
          <router-link to="/admin/chapter-roasts" class="nav-tab">Chapter Roasts</router-link>
          <router-link to="/admin/consults" class="nav-tab">Consults</router-link>
        </nav>
      </header>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
      </div>

      <template v-else>
        <div class="stats-row">
          <div class="stat-card">
            <div class="stat-value">{{ total }}</div>
            <div class="stat-label">Total responses</div>
          </div>
          <div class="stat-card">
            <div class="stat-value thumbs-up">{{ upCount }}</div>
            <div class="stat-label">👍 Thumbs up</div>
          </div>
          <div class="stat-card">
            <div class="stat-value thumbs-down">{{ downCount }}</div>
            <div class="stat-label">👎 Thumbs down</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ positivePercent }}%</div>
            <div class="stat-label">Positive</div>
          </div>
        </div>

        <div class="filter-row">
          <button
            v-for="opt in filterOptions"
            :key="opt.value"
            @click="activeFilter = opt.value"
            class="filter-btn"
            :class="{ active: activeFilter === opt.value }"
          >
            {{ opt.label }}
          </button>
        </div>

        <div v-if="filteredItems.length === 0" class="empty-state">No feedback yet</div>

        <div v-else class="feedback-list">
          <div v-for="item in filteredItems" :key="item.id" class="feedback-card">
            <div class="feedback-reaction" :class="item.reaction">
              {{ item.reaction === 'up' ? '👍' : '👎' }}
            </div>
            <div class="feedback-body">
              <div class="feedback-header">
                <a :href="`/edit/${item.projectId}`" target="_blank" class="project-link">
                  {{ item.projectTitle || 'Untitled' }}
                </a>
                <span class="output-badge">{{ formatOutputName(item.outputName) }}</span>
              </div>
              <p v-if="item.message" class="feedback-message">"{{ item.message }}"</p>
              <div class="feedback-meta">{{ item.projectEmail }} · {{ formatDate(item.createdAt) }}</div>

              <div v-if="canFeature(item)" class="feature-row">
                <button
                  class="feature-toggle"
                  :class="{ featured: !!item.testimonialId }"
                  :disabled="saving === item.id"
                  @click="item.testimonialId ? unfeature(item) : openEditor(item)"
                >
                  {{ item.testimonialId ? '★ Featured on landing page' : '☆ Feature as testimonial' }}
                </button>
                <button
                  v-if="item.testimonialId && editingId !== item.id"
                  class="feature-edit"
                  @click="openEditor(item)"
                >
                  Edit
                </button>
              </div>

              <div v-if="editingId === item.id" class="feature-editor">
                <label class="feature-label">
                  Quote
                  <textarea v-model="draftQuote" rows="3" maxlength="500" class="feature-input"></textarea>
                  <span class="feature-hint">{{ draftQuote.length }}/500 · trim it to the quotable sentence</span>
                </label>
                <label class="feature-label">
                  Display name
                  <input v-model="draftName" maxlength="80" class="feature-input" />
                  <span class="feature-hint">Use "A Twig writer" if the email doesn't give a real name</span>
                </label>
                <p v-if="editorError" class="feature-error">{{ editorError }}</p>
                <div class="feature-actions">
                  <button class="feature-save" :disabled="saving === item.id" @click="saveFeature(item)">
                    {{ saving === item.id ? 'Saving…' : 'Save' }}
                  </button>
                  <button class="feature-cancel" @click="closeEditor">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="total > items.length" class="load-more">
          <button @click="loadMore" :disabled="loadingMore" class="load-more-btn">
            {{ loadingMore ? 'Loading…' : `Load more (${total - items.length} remaining)` }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { suggestDisplayName } from '../lib/display-name';
import { useAuthStore } from '../stores/auth';

interface FeedbackItem {
  id: string;
  projectId: string;
  uid: string;
  frameworkId: string;
  outputName: string;
  reaction: 'up' | 'down';
  message: string | null;
  createdAt: string;
  projectTitle: string | null;
  projectEmail: string;
  testimonialId: string | null;
  testimonialQuote: string | null;
  testimonialName: string | null;
}

const authStore = useAuthStore();
const loading = ref(true);
const loadingMore = ref(false);
const items = ref<FeedbackItem[]>([]);
const total = ref(0);
const activeFilter = ref<'all' | 'up' | 'down' | 'comments' | 'featured'>('all');
const editingId = ref<string | null>(null);
const draftQuote = ref('');
const draftName = ref('');
const editorError = ref('');
const saving = ref<string | null>(null);

const filterOptions = [
  { label: 'All', value: 'all' as const },
  { label: '👍 Thumbs up', value: 'up' as const },
  { label: '👎 Thumbs down', value: 'down' as const },
  { label: 'With comments', value: 'comments' as const },
  { label: '★ Featured', value: 'featured' as const },
];

const upCount = computed(() => items.value.filter(i => i.reaction === 'up').length);
const downCount = computed(() => items.value.filter(i => i.reaction === 'down').length);
const positivePercent = computed(() => {
  if (total.value === 0) return 0;
  return Math.round((upCount.value / items.value.length) * 100);
});

const filteredItems = computed(() => {
  if (activeFilter.value === 'up') return items.value.filter(i => i.reaction === 'up');
  if (activeFilter.value === 'down') return items.value.filter(i => i.reaction === 'down');
  if (activeFilter.value === 'comments') return items.value.filter(i => i.message);
  if (activeFilter.value === 'featured') return items.value.filter(i => i.testimonialId);
  return items.value;
});

async function fetchFeedback(offset = 0) {
  const token = await authStore.getIdToken();
  const response = await fetch(`/api/admin/feedback?limit=50&offset=${offset}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch feedback');
  return response.json() as Promise<{ items: FeedbackItem[]; total: number }>;
}

async function loadInitial() {
  try {
    const data = await fetchFeedback(0);
    items.value = data.items;
    total.value = data.total;
  } catch (error) {
    console.error('Failed to load feedback:', error);
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  loadingMore.value = true;
  try {
    const data = await fetchFeedback(items.value.length);
    items.value.push(...data.items);
    total.value = data.total;
  } catch (error) {
    console.error('Failed to load more feedback:', error);
  } finally {
    loadingMore.value = false;
  }
}

/** Only positive feedback that actually said something can become a testimonial. */
function canFeature(item: FeedbackItem): boolean {
  return item.reaction === 'up' && !!item.message?.trim();
}

function openEditor(item: FeedbackItem) {
  editingId.value = item.id;
  editorError.value = '';
  draftQuote.value = item.testimonialQuote ?? item.message ?? '';
  draftName.value = item.testimonialName ?? suggestDisplayName(item.projectEmail);
}

function closeEditor() {
  editingId.value = null;
  editorError.value = '';
}

async function saveFeature(item: FeedbackItem) {
  const quote = draftQuote.value.trim();
  const name = draftName.value.trim();
  if (!quote || !name) {
    editorError.value = 'Both a quote and a display name are required.';
    return;
  }
  saving.value = item.id;
  editorError.value = '';
  try {
    const token = await authStore.getIdToken();
    const response = await fetch(`/api/admin/feedback/${item.id}/testimonial`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ quote, name }),
    });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error ?? 'Failed to save');
    const data = (await response.json()) as { testimonial: { id: string; quote: string; name: string } };
    item.testimonialId = data.testimonial.id;
    item.testimonialQuote = data.testimonial.quote;
    item.testimonialName = data.testimonial.name;
    closeEditor();
  } catch (error: any) {
    editorError.value = error.message ?? 'Failed to save testimonial';
  } finally {
    saving.value = null;
  }
}

async function unfeature(item: FeedbackItem) {
  saving.value = item.id;
  try {
    const token = await authStore.getIdToken();
    const response = await fetch(`/api/admin/feedback/${item.id}/testimonial`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to remove');
    item.testimonialId = null;
    item.testimonialQuote = null;
    item.testimonialName = null;
    if (editingId.value === item.id) closeEditor();
  } catch (error) {
    console.error('Failed to un-feature testimonial:', error);
  } finally {
    saving.value = null;
  }
}

function formatOutputName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

onMounted(loadInitial);
</script>

<style scoped>
.admin-feedback-page {
  flex: 1;
  background: #f7fafc;
}

.page-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.admin-nav {
  display: flex;
  gap: 0.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.nav-tab {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #718096;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s;
}

.nav-tab:hover {
  color: #1a202c;
}

.nav-tab.active,
.nav-tab.router-link-active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}

.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  text-align: center;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
}

.stat-value.thumbs-up {
  color: #22543d;
}

.stat-value.thumbs-down {
  color: #742a2a;
}

.stat-label {
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;
}

.filter-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 0.375rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #4a5568;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.filter-btn:hover {
  border-color: #cbd5e0;
  background: #f7fafc;
}

.filter-btn.active {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: white;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #718096;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.feedback-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.feedback-card {
  display: flex;
  gap: 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.25rem;
}

.feedback-reaction {
  font-size: 1.5rem;
  flex-shrink: 0;
  line-height: 1;
  padding-top: 0.125rem;
}

.feedback-body {
  flex: 1;
  min-width: 0;
}

.feedback-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.375rem;
}

.project-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-accent);
  text-decoration: none;
}

.project-link:hover {
  text-decoration: underline;
}

.output-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  background: #edf2f7;
  color: #4a5568;
}

.feedback-message {
  font-size: 0.875rem;
  color: #2d3748;
  margin: 0.375rem 0;
  font-style: italic;
}

.feedback-meta {
  font-size: 0.75rem;
  color: #a0aec0;
}

.feature-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.feature-toggle {
  background: none;
  border: 1px solid #cbd5e0;
  border-radius: 999px;
  padding: 0.2rem 0.7rem;
  font-size: 0.75rem;
  color: #4a5568;
  cursor: pointer;
}

.feature-toggle:hover:not(:disabled) {
  border-color: #a0aec0;
  color: #2d3748;
}

.feature-toggle.featured {
  background: #fffaf0;
  border-color: #f6ad55;
  color: #b7791f;
}

.feature-toggle:disabled {
  opacity: 0.6;
  cursor: default;
}

.feature-edit {
  background: none;
  border: none;
  font-size: 0.75rem;
  color: #718096;
  text-decoration: underline;
  cursor: pointer;
}

.feature-editor {
  margin-top: 0.625rem;
  padding: 0.75rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.feature-label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #4a5568;
}

.feature-input {
  font: inherit;
  font-size: 0.8125rem;
  color: #2d3748;
  padding: 0.4rem 0.5rem;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  resize: vertical;
}

.feature-hint {
  font-size: 0.6875rem;
  color: #a0aec0;
}

.feature-error {
  margin: 0;
  font-size: 0.75rem;
  color: #c53030;
}

.feature-actions {
  display: flex;
  gap: 0.5rem;
}

.feature-save {
  background: #2d3748;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.35rem 0.9rem;
  font-size: 0.75rem;
  cursor: pointer;
}

.feature-save:disabled {
  opacity: 0.6;
  cursor: default;
}

.feature-cancel {
  background: none;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  padding: 0.35rem 0.9rem;
  font-size: 0.75rem;
  color: #4a5568;
  cursor: pointer;
}

.load-more {
  text-align: center;
  margin-top: 1.5rem;
}

.load-more-btn {
  padding: 0.625rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.load-more-btn:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.load-more-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
